import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Calendar, Home, Phone, Mail, CreditCard, AlertCircle } from 'lucide-react';
import { api } from '@/App';

const BookingConfirmed = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingNumber = searchParams.get('booking');
  const isPaid = searchParams.get('paid') === 'true';
  const paymentPending = searchParams.get('payment_pending') === 'true';
  
  // Initialize portal settings from cache to prevent flash
  const [portalSettings, setPortalSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('portal_settings_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.primary_color && parsed.secondary_color) {
          return parsed;
        }
      }
    } catch (e) {}
    return {
      portal_name: 'Book Your Stay',
      primary_color: '#d4a373',
      secondary_color: '#2d3748'
    };
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/portal/settings');
        const newSettings = { ...portalSettings, ...response.data };
        setPortalSettings(newSettings);
        localStorage.setItem('portal_settings_cache', JSON.stringify(newSettings));
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div 
        className="py-4 px-4 shadow-sm"
        style={{ backgroundColor: portalSettings.secondary_color }}
      >
        <div className="max-w-6xl mx-auto">
          <span className="font-semibold text-white">{portalSettings.portal_name}</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <CardContent className="p-8 text-center">
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: `${portalSettings.primary_color}20` }}
            >
              <CheckCircle className="h-12 w-12" style={{ color: portalSettings.primary_color }} />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
            <p className="text-gray-600 mb-6">
              Your booking has been successfully confirmed.
            </p>
            
            {bookingNumber && (
              <div className="bg-gray-100 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">Booking Reference</p>
                <p className="text-2xl font-mono font-bold" style={{ color: portalSettings.primary_color }}>
                  {bookingNumber}
                </p>
              </div>
            )}
            
            {/* Payment Status */}
            {isPaid && (
              <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                <CreditCard className="h-5 w-5 text-green-600" />
                <span className="text-green-700 font-medium">Payment Successful</span>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
            )}
            
            {paymentPending && (
              <div className="flex items-center justify-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <span className="text-yellow-700 font-medium">Payment Pending - Pay at Property</span>
              </div>
            )}
            
            <div className="text-left bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-blue-800 mb-2">What&apos;s Next?</h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Confirmation email has been sent to your email address
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  The property may contact you for any additional details
                </li>
                <li className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Please arrive on time for a smooth check-in experience
                </li>
              </ul>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
              >
                <Home className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/my-bookings')}
              >
                My Bookings
              </Button>
              <Button 
                style={{ backgroundColor: portalSettings.primary_color }}
                onClick={() => navigate('/search')}
              >
                Book Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingConfirmed;
